import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-multi-lang";

import Modal from "../../../components/Modal/Modal";
import { Col, Button } from "react-bootstrap";
import { InputField } from "../../../components/FormElements/FormElements";
import ClientApi from "../../../services/api/clients";
import { toast } from "react-toastify";

interface IProps {
  isOpen: boolean;
  toggle: () => void;
  client_id: number;
  year: number;
  quarter: string;
  client_index?: number; // ✅ THIS MUST BE ADDED
}

export default function ForceGradeModel({
  isOpen,
  toggle,
  client_id,
  year,
  quarter,
  client_index, // ✅ THIS MUST BE ADDED
}: IProps) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const api = new ClientApi();

  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedYear, setSelectedYear] = useState<number | null>(2025);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Determine if client is a bank (index == 8)
  const isBank = client_index === 8;

  useEffect(() => {
    if (isOpen) {
      setSelectedYear(year || null);
      setSelectedQuarter(quarter || null);
      setInputValue("");
      setError("");
    }
  }, [isOpen]);

  const handleInputChange = (e: any) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Only validate as number if NOT a bank
    if (!isBank && value && isNaN(Number(value))) {
      setError(t("please_enter_valid_number"));
    } else {
      setError("");
    }
  };

  const handleUpdate = async () => {
    // Validate year and quarter
    if (!selectedYear || !selectedQuarter) {
      toast.error(t("please_select_year_and_quarter"));
      return;
    }

    // Validate input
    if (!inputValue || inputValue.trim() === "") {
      toast.error(t("please_enter_force_grade"));
      return;
    }

    // Prepare the gradeId based on client type
    let gradeId: any;
    
    if (isBank) {
      // For banks (index == 8): send as string (character)
      gradeId = inputValue.trim();
    } else {
      // For non-banks: convert to number
      gradeId = Number(inputValue);
      
      // Validate it's a valid number
      if (isNaN(gradeId)) {
        toast.error(t("please_enter_valid_number"));
        return;
      }
    }

    try {
      setLoading(true);

      const response = await api.clients().setForceGrade({
        client_id: client_id,
        gradeId: gradeId,
        year: selectedYear,
        quarter: selectedQuarter,
      });

      console.log(response);

      if (response?.status === 200 && response.data?.success) {
        toast.success(response.data.message || t("success"));
        setInputValue("");
        setError("");
        toggle();
      } else {
        const errorMessage = response?.data?.message || t("unexpected_error");
        toast.error(errorMessage);
      }

    } catch (err: any) {
      console.error(err);
      const errorMessage = err?.response?.data?.message || err?.message || t("error_occurred");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={isOpen} toggle={toggle}>
      <h4 className="mb-4">
        {t("Set Force Grade")} 
      </h4>
      <h4 className="mb-4">
        {selectedYear}/{selectedQuarter} 
        {isBank && <span className="badge bg-info ms-2">Bank</span>}
      </h4>

      <div className="row g-3">
        <Col md={4}>
          <InputField
            value={inputValue}
            onChange={handleInputChange}
            placeholder={isBank ? t("enter Force Grade (text)") : t("enter Force Grade (number)")}
            type="text"
            className={error ? "is-invalid" : ""}
          />
          {error && (
            <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
              {error}
            </div>
          )}
          {isBank && (
            <div className="text-muted mt-1" style={{ fontSize: "0.875rem" }}>
              {t("banks_accept_text_values")}
            </div>
          )}
        </Col>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <Button
          variant="primary"
          onClick={handleUpdate}
          disabled={loading || !!error || !inputValue}
        >
          {loading ? t("saving") : t("save")}
        </Button>
      </div>
    </Modal>
  );
}